import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  addNavFavorite,
  fetchNavMenu,
  isNavItemLocked,
  removeNavFavorite,
} from "../api/scaNav";
import { useAuthOptional } from "./AuthContext";
import { appPathname } from "../utils/contractPath";
import {
  navRouteMatches,
  scoreNavRouteMatch,
} from "../utils/navRouteMatch";

const NavContext = createContext(undefined);

/** Melhor item de menu para pathname (+ search com query, ex. ?nature=OWN). */
export function findMenuItemForPath(sections, pathname, search = "") {
  const path = appPathname(pathname);
  let best = null;
  let bestScore = -1;
  for (const sec of sections || []) {
    for (const item of sec.items || []) {
      const route = String(item?.ds_route || "").trim();
      if (!route) continue;
      if (!navRouteMatches(route, path, search)) continue;
      const score = scoreNavRouteMatch(route);
      if (score > bestScore) {
        best = item;
        bestScore = score;
      }
    }
  }
  return best;
}

/**
 * Catálogo de navegação partilhado (lateral + favorito no header).
 * Vive acima do AppShell para não remontar / refetch a cada mudança de rota.
 */
export function NavProvider({ children }) {
  const auth = useAuthOptional();
  const authReady = auth?.authReady;
  const isAuthenticated = Boolean(auth?.user);
  const sessionKey = `${auth?.user?.id_user ?? ""}:${auth?.user?.nm_login ?? ""}`;
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [favBusy, setFavBusy] = useState("");
  const sectionsRef = useRef(sections);
  sectionsRef.current = sections;

  const refresh = useCallback(async () => {
    // Evita «A carregar…» no sidebar quando já há catálogo (favoritos / soft refresh).
    if (sectionsRef.current.length === 0) setLoading(true);
    setError("");
    try {
      const data = await fetchNavMenu();
      setSections(Array.isArray(data?.sections) ? data.sections : []);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao carregar menus.");
      setSections([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Recarrega com sessão autenticada; limpa fora de login.
  useEffect(() => {
    if (auth && authReady === false) return undefined;
    if (!isAuthenticated) {
      setSections([]);
      setLoading(false);
      setError("");
      return undefined;
    }
    setSections([]);
    setLoading(true);
    void refresh();
    return undefined;
  }, [refresh, sessionKey, auth, authReady, isAuthenticated]);

  const toggleFavorite = useCallback(
    async (item) => {
      if (!item?.cd_menu || !item.fl_allow_favorite || favBusy) return;
      if (isNavItemLocked(item)) return;
      setFavBusy(item.cd_menu);
      try {
        if (item.fl_favorite) {
          await removeNavFavorite(item.cd_menu);
        } else {
          await addNavFavorite(item.cd_menu);
        }
        await refresh();
      } catch (e) {
        setError(
          e instanceof Error ? e.message : "Falha ao atualizar favorito."
        );
        throw e;
      } finally {
        setFavBusy("");
      }
    },
    [favBusy, refresh]
  );

  const value = useMemo(
    () => ({
      sections,
      loading,
      error,
      setError,
      refresh,
      toggleFavorite,
      favBusy,
      findMenuItemForPath: (pathname, search = "") =>
        findMenuItemForPath(sections, pathname, search),
    }),
    [sections, loading, error, refresh, toggleFavorite, favBusy]
  );

  return <NavContext.Provider value={value}>{children}</NavContext.Provider>;
}

export function useNav() {
  const ctx = useContext(NavContext);
  if (ctx === undefined) {
    throw new Error("useNav deve ser usado dentro de NavProvider");
  }
  return ctx;
}

/** Safe fora do shell (ex.: testes) — devolve null se sem provider. */
export function useNavOptional() {
  return useContext(NavContext);
}
